import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Send, Check, Loader2, Users, Type, Image as ImageIcon, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_URL as API, BACKEND_URL } from '@/utils/apiConfig';
import { extractFollowupChips } from '@/utils/extractFollowupChips';
import { toast } from 'sonner';
import axios from 'axios';

/**
 * ShareToFriendModal — pick a friend and send them a chat AI response or
 * a daily reflection. Two delivery variants:
 *   • Text (default)  — the cleaned response is dropped into the
 *     friend's shared-thread as a text bubble.
 *   • Image           — we ask the backend to mint a `share_id`
 *     (`/api/share`) which serves a server-rendered OG image at
 *     `/api/share/{share_id}/og-image`. We attach that absolute URL to
 *     the friend share so the recipient sees a beautifully styled image
 *     card. Reflections also support this variant.
 *
 * The visible preview text is ALWAYS sanitised through
 * `extractFollowupChips()` so the LLM's machine-readable
 * `<!--FOLLOWUP_CHIPS:[...]-->` marker never leaks into the share.
 */

const IMAGE_THEMES = [
  { key: 'classic', label: 'Classic', bg: '#FFFFFF', text: '#1a1a2e', accent: '#6366f1', secondary: '#64748b', border: '#e2e8f0' },
  { key: 'dark', label: 'Dark', bg: '#0f172a', text: '#f1f5f9', accent: '#818cf8', secondary: '#94a3b8', border: '#1e293b' },
  { key: 'scripture', label: 'Scripture', bg: '#fef3c7', text: '#451a03', accent: '#92400e', secondary: '#78350f', border: '#fbbf24' },
];

export default function ShareToFriendModal({ message, conversationTitle, reflection, onClose }) {
  const { t } = useTranslation();
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null); // friend user_id being sent to
  const [sent, setSent] = useState(new Set());
  const [note, setNote] = useState('');
  const [mode, setMode] = useState('text'); // 'text' | 'image'
  const [imageTheme, setImageTheme] = useState('classic');
  const [shareId, setShareId] = useState(null);
  const [shareSummary, setShareSummary] = useState(null);
  const [generatingShare, setGeneratingShare] = useState(false);

  // Sanitise the source content ONCE — this strips the
  // `<!--FOLLOWUP_CHIPS:[...]-->` marker so it never leaks into
  // shared bubbles. Both the preview AND the payload use this.
  const cleanContent = useMemo(() => {
    const raw = reflection ? (reflection.body || reflection.content || '') : (message?.content || '');
    return extractFollowupChips(raw).content;
  }, [reflection, message]);

  const themeMeta = IMAGE_THEMES.find((th) => th.key === imageTheme) || IMAGE_THEMES[0];
  const imageEnabled = mode === 'image' && Boolean(message?.message_id);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/community/friends`, { withCredentials: true });
        setFriends(res.data.friends || []);
      } catch {
        toast.error(t('share.failed_load_friends'));
      }
      setLoading(false);
    };
    load();
  }, [t]);

  // Eagerly prepare the server-rendered share image as soon as the user
  // toggles to "Image" mode. The same `share_id` is reused for every
  // recipient — efficient, idempotent for the same theme.
  const ensureImageShare = useCallback(async (themeKey) => {
    if (!message?.message_id) return null;
    setGeneratingShare(true);
    try {
      const res = await axios.post(
        `${API}/share`,
        { message_id: message.message_id, theme: themeKey },
        { withCredentials: true },
      );
      setShareId(res.data.share_id);
      if (res.data.summary) setShareSummary(res.data.summary);
      return res.data.share_id;
    } catch {
      toast.error(t('share.share_failed') || 'Could not prepare image');
      return null;
    } finally {
      setGeneratingShare(false);
    }
  }, [message?.message_id, t]);

  useEffect(() => {
    if (mode === 'image' && message?.message_id && !shareId) {
      ensureImageShare(imageTheme);
    }
  }, [mode, imageTheme, message?.message_id, shareId, ensureImageShare]);

  // Re-mint the server image when the theme changes mid-session.
  const handleThemeChange = (key) => {
    setImageTheme(key);
    setShareId(null);  // invalidate so the next ensure() re-creates with the new theme
  };

  const handleSend = async (friendId) => {
    setSending(friendId);
    try {
      const payload = {
        friend_user_id: friendId,
        note: note.trim(),
      };
      if (reflection) {
        payload.reflection_id = reflection.reflection_id;
        payload.reflection_topic = reflection.topic || '';
        payload.reflection_date = reflection.date || '';
        payload.content = cleanContent;
        payload.conversation_title = `Daily Reflection: ${reflection.topic || ''}`;
      } else if (message) {
        payload.message_id = message.message_id;
        payload.content = cleanContent;  // sanitised — no FOLLOWUP_CHIPS leak
        payload.question = message._question || '';
        payload.conversation_title = conversationTitle || '';
      }
      // Image-share variant attaches the server-rendered card URL so
      // the recipient sees a stylised image bubble. If the share-id
      // mint fails we surface that explicitly rather than silently
      // downgrading to a text-only share.
      if (imageEnabled) {
        let id = shareId;
        if (!id) id = await ensureImageShare(imageTheme);
        if (!id) {
          toast.error(t('generated.sharetofriendmodal_could_not_prepare_7ce8'));
          setSending(null);
          return;
        }
        payload.share_id = id;
        payload.share_image_url = `${BACKEND_URL}/api/share/${id}/og-image`;
      }
      await axios.post(`${API}/community/share`, payload, { withCredentials: true });
      setSent((prev) => new Set([...prev, friendId]));
      toast.success(t('share.shared'));
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : t('share.failed_share'));
    }
    setSending(null);
  };

  // Preview text — pulls from reflection or sanitised cleanContent.
  const previewSource = reflection
    ? (reflection.topic ? `Daily Reflection: ${reflection.topic}` : cleanContent)
    : cleanContent;
  const preview = previewSource.replace(/[#*_`]/g, '').slice(0, 100);
  const previewOverflows = previewSource.length > 100;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      {/* Modal */}
      <div
        className="relative w-full sm:max-w-md mx-auto rounded-t-2xl sm:rounded-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
        style={{ background: 'var(--theme-surface)', border: '1px solid var(--glass-border)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="share-to-friend-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
          <h3 className="text-base font-semibold" style={{ color: 'var(--theme-text)' }}>{t('generated.sharetofriendmodal_share_with_friend_531c')}</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[var(--theme-hover)]" data-testid="close-share-friend-modal">
            <X className="h-5 w-5" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
        </div>

        {/* Mode toggle — only when sharing a chat message (reflections
            don't have message_id so the image variant isn't available). */}
        {message?.message_id && (
          <div className="px-5 py-3" style={{ borderBottom: '1px solid var(--glass-border)' }}>
            <div
              className="inline-flex p-1 rounded-full"
              style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
              data-testid="share-friend-mode-toggle"
              role="radiogroup"
              aria-label={t('generated.sharetofriendmodal_share_format_08ff')}
            >
              <button
                type="button"
                role="radio"
                aria-checked={mode === 'text'}
                onClick={() => setMode('text')}
                className="px-4 py-1.5 rounded-full text-xs font-medium transition flex items-center gap-1.5"
                style={mode === 'text'
                  ? { background: 'var(--theme-accent)', color: '#fff' }
                  : { color: 'var(--theme-textSecondary)' }}
                data-testid="share-friend-mode-text"
              >
                <Type className="h-3.5 w-3.5" />
                {t('generated.sharetofriendmodal_text_df12')}
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={mode === 'image'}
                onClick={() => setMode('image')}
                className="px-4 py-1.5 rounded-full text-xs font-medium transition flex items-center gap-1.5"
                style={mode === 'image'
                  ? { background: 'var(--theme-accent)', color: '#fff' }
                  : { color: 'var(--theme-textSecondary)' }}
                data-testid="share-friend-mode-image"
              >
                <ImageIcon className="h-3.5 w-3.5" />
                {t('generated.sharetofriendmodal_image_dad5')}
              </button>
            </div>
          </div>
        )}

        {/* Preview — text or image card */}
        <div className="px-5 py-3" style={{ borderBottom: '1px solid var(--glass-border)' }}>
          <p className="text-xs font-medium mb-2" style={{ color: 'var(--theme-textSecondary)' }}>
            {imageEnabled ? 'Image preview' : 'Sharing'}
          </p>
          {imageEnabled ? (
            <div className="space-y-3">
              <div className="flex gap-2 flex-wrap" data-testid="share-friend-image-themes">
                {IMAGE_THEMES.map((th) => {
                  const active = imageTheme === th.key;
                  return (
                    <button
                      key={th.key}
                      type="button"
                      onClick={() => handleThemeChange(th.key)}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition"
                      style={{
                        background: active ? 'color-mix(in srgb, var(--theme-accent) 18%, transparent)' : 'var(--glass-bg)',
                        border: `1px solid ${active ? 'var(--theme-accent)' : 'var(--glass-border)'}`,
                        color: active ? 'var(--theme-accent)' : 'var(--theme-textSecondary)',
                      }}
                      data-testid={`share-friend-theme-${th.key}`}
                    >
                      <span className="h-3 w-3 rounded-full" style={{ background: th.accent }} />
                      {th.label}
                    </button>
                  );
                })}
              </div>
              <div
                className="rounded-xl overflow-hidden"
                style={{ border: '1px solid var(--glass-border)' }}
                data-testid="share-friend-image-preview"
              >
                {generatingShare && !shareSummary ? (
                  <div className="flex items-center justify-center py-10" style={{ background: themeMeta.bg }}>
                    <Loader2 className="h-5 w-5 animate-spin" style={{ color: themeMeta.accent }} />
                  </div>
                ) : (
                  <ShareImageCard theme={themeMeta} text={shareSummary || cleanContent} />
                )}
              </div>
              <p className="text-[11px] flex items-center gap-1.5" style={{ color: 'var(--theme-textSecondary)' }}>
                <Sparkles className="h-3 w-3" style={{ color: 'var(--theme-accent)' }} />
                {t('generated.sharetofriendmodal_sent_as_a_8708')}
              </p>
            </div>
          ) : (
            <p className="text-sm line-clamp-2" style={{ color: 'var(--theme-text)' }} data-testid="share-friend-text-preview">
              {preview}{previewOverflows ? '...' : ''}
            </p>
          )}
        </div>

        {/* Optional note */}
        <div className="px-5 py-3" style={{ borderBottom: '1px solid var(--glass-border)' }}>
          <input
            type="text"
            placeholder={t('share.add_note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full bg-transparent text-sm outline-none"
            style={{ color: 'var(--theme-text)' }}
            maxLength={200}
            data-testid="share-friend-note-input"
          />
        </div>

        {/* Friends list */}
        <div className="px-5 py-3 max-h-[280px] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin" style={{ color: 'var(--theme-accent)' }} />
            </div>
          ) : friends.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-10 w-10 mx-auto mb-2" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }} />
              <p className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('generated.sharetofriendmodal_no_friends_yet_bccd')}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {friends.map((f) => {
                const initial = (f.name || f.username || '?')[0].toUpperCase();
                const isSent = sent.has(f.user_id);
                const isSending = sending === f.user_id;
                const disabled = isSending || isSent || (imageEnabled && generatingShare);

                return (
                  <div
                    key={f.user_id}
                    className="flex items-center gap-3 p-2.5 rounded-xl transition-all"
                    style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
                    data-testid={`share-friend-${f.user_id}`}
                  >
                    {f.picture ? (
                      <img src={f.picture} alt="" className="h-9 w-9 rounded-full object-cover shrink-0" />
                    ) : (
                      <div
                        className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                        style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-accent)' }}
                      >
                        {initial}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--theme-text)' }}>
                        {f.name || t('feed.unknown')}
                      </p>
                      {f.username && (
                        <p className="text-xs truncate" style={{ color: 'var(--theme-textSecondary)' }}>
                          @{f.username}
                        </p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      className="h-8 rounded-full text-xs px-3"
                      style={
                        isSent
                          ? { background: '#22c55e', color: '#fff' }
                          : { background: 'var(--theme-accent)', color: '#fff' }
                      }
                      onClick={() => !disabled && handleSend(f.user_id)}
                      disabled={disabled}
                      data-testid={`send-to-friend-${f.user_id}`}
                    >
                      {isSent ? (
                        <><Check className="h-3 w-3 mr-1" />{t('generated.sharetofriendmodal_sent_307b')}</>
                      ) : isSending ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <><Send className="h-3 w-3 mr-1" />{imageEnabled ? 'Send Image' : 'Send'}</>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Bottom padding for safe area */}
        <div className="h-2" style={{ paddingBottom: 'max(0.5rem, var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)))' }} />
      </div>
    </div>
  );
}

/* In-modal preview of the server-rendered share card. The actual
   delivered image comes from `/api/share/{share_id}/og-image` — this
   is a faithful local approximation so the user knows what they're
   sending. */
function ShareImageCard({ theme, text }) {
  const {
    t
  } = useTranslation();

  return (
    <div
      style={{
        background: theme.bg,
        padding: '20px 18px 16px',
        fontFamily: "'Roboto', -apple-system, sans-serif",
        position: 'relative',
      }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: theme.accent }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: theme.accent }}>{t('generated.sharetofriendmodal_eili_6d36')}</span>
        <span style={{ fontSize: 10, color: theme.secondary }}>{t('generated.sharetofriendmodal_the_word_first_78d5')}</span>
      </div>
      <div style={{ height: 1, background: theme.border, marginBottom: 10 }} />
      <div
        style={{
          fontSize: 12,
          lineHeight: 1.6,
          color: theme.text,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: 180,
          overflow: 'hidden',
        }}
      >
        {text || 'Generating preview...'}
      </div>
      <div style={{ marginTop: 12, paddingTop: 8, borderTop: `1px solid ${theme.border}` }}>
        <span style={{ fontSize: 10, color: theme.secondary }}>{t('generated.sharetofriendmodal_tap_link_for_7033')}</span>
      </div>
    </div>
  );
}
